import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import {
  clubPortalBase,
  formatPortalEventDate,
  secretaryPortalBase
} from './portalPaths';

const PortalEventSwitcher = ({ session, kind }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState('');
  const events = useMemo(
    () => (Array.isArray(session?.events) ? session.events : []),
    [session]
  );
  const currentEventId = session?.event?.id;
  const currentName = session?.event?.name || 'BowlScout event';
  const showSearch = events.length > 6;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return events;
    return events.filter((event) => String(event.name || '').toLowerCase().includes(term));
  }, [events, query]);

  const close = () => {
    setAnchorEl(null);
    setQuery('');
  };

  const openEvent = (event) => {
    if (!event?.id) return;
    if (kind === 'club' && !event.venueId) return;

    close();
    navigate(
      kind === 'club'
        ? clubPortalBase(event.id, event.venueId)
        : secretaryPortalBase(event.id)
    );
  };

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        aria-label="Switch event"
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{
          minWidth: 0,
          maxWidth: { xs: 190, sm: 380 },
          justifyContent: 'flex-start',
          px: 0,
          textTransform: 'none'
        }}>
        <Typography variant="subtitle1" fontWeight="bold" noWrap>
          {currentName}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        MenuListProps={{ sx: { maxHeight: 420 } }}
        PaperProps={{
          sx: { width: 'min(360px, 92vw)', maxWidth: '92vw' }
        }}>
        {showSearch && (
          <Box sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search events"
              autoFocus
              inputProps={{ 'aria-label': 'Search events' }}
            />
          </Box>
        )}

        {filtered.length === 0 && <MenuItem disabled>No events found</MenuItem>}

        {filtered.map((event) => {
          const date = formatPortalEventDate(event.startDate || event.endDate);
          const current = event.id === currentEventId;
          const selectable = kind !== 'club' || Boolean(event.venueId);

          return (
            <MenuItem
              key={event.id}
              selected={current}
              disabled={!selectable}
              onClick={() => openEvent(event)}
              sx={{
                minHeight: 44,
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 0.25,
                py: 1
              }}>
              <Typography variant="body2" fontWeight={current ? 'bold' : 'regular'} noWrap>
                {event.name || 'Event'}
              </Typography>
              {date && (
                <Typography variant="caption" color="text.secondary">
                  {date}
                </Typography>
              )}
              {current && (
                <Typography variant="caption" color="primary">
                  Current event
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default PortalEventSwitcher;
